import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../..';
import ClaimPayments, { getLatestPaymentOrderId } from '../../../components/ClaimPayments';
import { getClaimStripePaymentsStateByClaimId } from '../../../redux/claim-stripe-payments/claim-stripe-payments.selectors';
import { fetchClaimStripePaymentsThunk } from '../../../redux/claim-stripe-payments/claim-stripe-payments.thunk';
import { getOrderAmountRemainingForOrder } from '../../../redux/order/order.selector';
import { fetchAmountRemainingForPaymentPlanOrderThunk } from '../../../redux/order/order.thunk';
import { getPendingPaymentPlanOrder } from '../../../redux/payment-plan/payment-plan.selectors';
import { AppState } from '../../../redux/root.reducer';
import { getFullClaimById } from '../../../redux/selectors/claim.selectors';
import { getClaimPaymentOrderFilterOptions, getClaimPaymentOrderSummaryFilterOptions } from './order-filter-options';

interface ClaimPaymentsContainerProps {
  claimId: string;
}

interface SelectedSummaryOrder {
  orderId?: string;
  includePaymentPlanInterest: boolean;
}

interface SelectedOrderAmountRemaining {
  orderId?: string;
  amountRemaining?: number;
}

const ClaimPaymentsContainer: React.FC<ClaimPaymentsContainerProps> = ({ claimId }) => {
  const dispatch = useAppDispatch();
  const claimPaymentsState = useSelector((state: AppState) => getClaimStripePaymentsStateByClaimId(state, claimId));
  const payments = claimPaymentsState ? claimPaymentsState.payments : [];
  const stripePaymentTotal = claimPaymentsState
    ? claimPaymentsState.stripePaymentTotal
    : { totalCharged: 0, totalCaptured: 0, hasManualRefundOrDisputeHandling: false, paymentCount: 0 };
  const stripePaymentTotalsByOrder = claimPaymentsState ? claimPaymentsState.stripePaymentTotalsByOrder : [];
  const claim = useSelector((state: AppState) => getFullClaimById(state, claimId));
  const activeOrder = useSelector(getPendingPaymentPlanOrder(claimId));
  const activeOrderId = activeOrder ? activeOrder.id : undefined;
  const activeOrderAmountRemaining = useSelector(getOrderAmountRemainingForOrder(activeOrderId));
  const lastActiveOrderId = activeOrderId ? undefined : getLatestPaymentOrderId(payments);
  const [selectedSummaryOrder, setSelectedSummaryOrder] = useState<SelectedSummaryOrder>({ includePaymentPlanInterest: false });
  const [selectedOrderAmountRemaining, setSelectedOrderAmountRemaining] = useState<SelectedOrderAmountRemaining>({});
  const orderFilterOptions = getClaimPaymentOrderFilterOptions(claim, activeOrderId);
  const summaryOrderFilterOptions = getClaimPaymentOrderSummaryFilterOptions(claim, activeOrderId);
  const hasFetched = claimPaymentsState ? claimPaymentsState.hasFetched : false;
  const selectedSummaryOrderId = selectedSummaryOrder.orderId;
  const summaryOrderAmountRemaining =
    selectedSummaryOrderId && selectedSummaryOrderId === activeOrderId
      ? activeOrderAmountRemaining
      : selectedOrderAmountRemaining.amountRemaining;
  const summaryOrderAmountRemainingOrderId =
    selectedSummaryOrderId && selectedSummaryOrderId === activeOrderId ? activeOrderId : selectedOrderAmountRemaining.orderId;

  const handleSummaryOrderChange = useCallback((orderId?: string, includePaymentPlanInterest = false) => {
    setSelectedSummaryOrder((currentSelectedSummaryOrder) => {
      if (
        currentSelectedSummaryOrder.orderId === orderId &&
        currentSelectedSummaryOrder.includePaymentPlanInterest === includePaymentPlanInterest
      ) {
        return currentSelectedSummaryOrder;
      }

      return { orderId, includePaymentPlanInterest };
    });
  }, []);

  useEffect(() => {
    if (!hasFetched) {
      dispatch(fetchClaimStripePaymentsThunk({ claimId }));
    }
  }, [claimId, hasFetched, dispatch]);

  useEffect(() => {
    if (!selectedSummaryOrderId || selectedSummaryOrderId === activeOrderId) {
      setSelectedOrderAmountRemaining({});
      return;
    }

    setSelectedOrderAmountRemaining({ orderId: selectedSummaryOrderId });
    let isCurrentRequest = true;
    dispatch(
      fetchAmountRemainingForPaymentPlanOrderThunk({
        orderId: selectedSummaryOrderId,
        includeInterest: selectedSummaryOrder.includePaymentPlanInterest,
      }),
    ).then((action) => {
      if (isCurrentRequest && fetchAmountRemainingForPaymentPlanOrderThunk.fulfilled.match(action)) {
        setSelectedOrderAmountRemaining({ orderId: selectedSummaryOrderId, amountRemaining: action.payload });
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [activeOrderId, selectedSummaryOrder.includePaymentPlanInterest, selectedSummaryOrderId, dispatch]);

  return (
    <ClaimPayments
      payments={payments}
      stripePaymentTotal={stripePaymentTotal}
      stripePaymentTotalsByOrder={stripePaymentTotalsByOrder}
      isLoading={claimPaymentsState ? claimPaymentsState.isLoading : false}
      error={claimPaymentsState ? claimPaymentsState.error : undefined}
      activeOrderId={activeOrderId}
      activeOrderStatusId={activeOrder ? activeOrder.statusId : undefined}
      lastActiveOrderId={lastActiveOrderId}
      summaryOrderAmountRemaining={summaryOrderAmountRemaining}
      summaryOrderAmountRemainingOrderId={summaryOrderAmountRemainingOrderId}
      summaryOrderFilterOptions={summaryOrderFilterOptions}
      orderFilterOptions={orderFilterOptions}
      showAmountRemainingAccuracyWarning={!claim || !claim.bankTransactionsEnabledAt}
      onSummaryOrderChange={handleSummaryOrderChange}
    />
  );
};

export default ClaimPaymentsContainer;
